<template>
  <div class="account-page container py-4">
    <div class="skip-links">
      <a href="#main-content" class="skip-link">Zum Hauptinhalt</a>
    </div>

    <div class="container-fluid p-4" id="main-content">
      <h1 class="mb-4"><font-awesome-icon icon="credit-card" /> Account</h1>

      <!-- Current Balance and Purchase -->
      <div class="row mb-4">
        <div class="col-lg-4">
          <div class="card shadow-sm">
            <div class="card-body text-center">
              <h3 class="card-title mb-2">Aktuelles Guthaben</h3>
              <div class="mb-2">
                <font-awesome-icon icon="coins" class="fa-2x text-warning mb-2" />
              </div>
              <div class="mb-2 d-flex flex-column align-items-center">
                <span class="h2 fw-bold text-primary">{{ user?.account?.amount || 0 }}</span>
                <span class="text-muted ms-1">Quittungen verfügbar</span>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-8">
          <div class="card shadow-sm">
            <div class="card-body">
              <h2 class="card-title ">Quittungen kaufen</h2>
              <p class="text-muted mb-3">Erweitere dein Guthaben</p>
              <button class="btn btn-primary btn-lg w-100" @click="showPurchaseDialog">
                <font-awesome-icon icon="shopping-cart" class="me-2" />
                Jetzt kaufen
              </button>
              <div class="mt-2 text-center">
                <button class="btn  btn-outline-primary btn-sm" @click="showPricingInfoModal">
                  <font-awesome-icon icon="info-circle" class="me-1" />
                  Preisübersicht anzeigen
                </button>
                <span class="text-muted mx-2">|</span>
                <button class="btn  btn-outline-primary btn-sm" @click="showCouponPurchaseDialog">
                  <font-awesome-icon icon="ticket" class="me-1" />
                  Gutschein eingeben
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pro Team Features Info -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card border-0 bg-gradient-primary text-white shadow-sm">
            <div class="card-body">
              <div class="row align-items-center">
                <div class="col-md-2 text-center">
                  <font-awesome-icon icon="crown" size="3x" class="text-warning" />
                </div>
                <div class="col-md-8">
                  <h4 class="card-title mb-2 text-white">Erweitere deine Berichsfunktionen mit einem Pro-Paket</h4>
                  <p class="card-text text-white-50 mb-3">
                    Mit einem Pro-Paket kannst du deine Quittungen individualisieren, eigene Felder erstellen und vieles mehr.
                  </p>
                  <div class="d-flex flex-wrap gap-2">
                    <span class="badge bg-warning text-dark">
                      <font-awesome-icon icon="paint-brush" class="me-1" />
                      Individualisierung
                    </span>
                    <span class="badge bg-warning text-dark">
                      <font-awesome-icon icon="list-alt" class="me-1" />
                      Eigene Felder
                    </span>
                    <span class="badge bg-warning text-dark">
                      <font-awesome-icon icon="image" class="me-1" />
                      Logo & Branding
                    </span>
                    <span class="badge bg-warning text-dark">
                      <font-awesome-icon icon="money-bill-wave" class="me-1" />
                      Erstattungen
                    </span>
                  </div>
                </div>
                <div class="col-md-2 text-end">
                  <router-link to="/sales" class="btn btn-warning btn-lg">
                    <font-awesome-icon icon="crown" class="me-2" />
                    Upgrade kaufen
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Personal Consumption History -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card shadow-sm">
            <div class="card-body">
              <PersonalConsumptionHistory />
            </div>
          </div>
        </div>
      </div>

      <!-- Purchase History -->
      <PurchaseHistory ref="purchaseHistoryRef" :limit="10" :show-title="true"  />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { showError, showSuccess } from '@/services/notification'
import { storeToRefs } from 'pinia'
import { 
  purchaseCoins as purchaseCoinsApi, 
} from '@/api/billing'
import { pricingApi } from '@/api/pricing'
import { teamService } from '@/api/teams'
import PersonalConsumptionHistory from '@/components/coins/PersonalConsumptionHistory.vue'
import PurchaseHistory from '@/components/PurchaseHistory.vue'

const router = useRouter()
const authStore = useAuthStore()
const { user } = storeToRefs(authStore)

const purchasing = ref(false)
const coinPackages = ref([])
const loadingPackages = ref(false)
const purchaseHistoryRef = ref(null)

const getStandardPackages = () => [
  { coins: 10, price: 1.42, pricePerCoin: 0.142, rule: null }, 
  { coins: 25, price: 14.16, pricePerCoin: 0.142, rule: null }, 
  { coins: 40, price: 141.60, pricePerCoin: 0.142, rule: null } 
]

const loadPricingRules = async () => {
  try {
    loadingPackages.value = true
    const response = await pricingApi.getCoinPricingRules()
    
    const rules = Array.isArray(response) ? response : (response.data || response.rules || [])
    
    if (!Array.isArray(rules)) {
      console.error('Invalid pricing rules response:', response)
      showError('Ungültige Antwort von der Preisregel-API')
      coinPackages.value = []
      return
    }
    
    const standardCoinAmounts = [10, 25, 40, 100]
    
    if (rules.length > 0) {
      coinPackages.value = standardCoinAmounts.map(coins => {
        const matchingRule = rules.find(rule => {
          const minCoins = rule.minCoins || 0
          const maxCoins = rule.maxCoins || Infinity
          return coins >= minCoins && coins <= maxCoins
        })
        
        if (matchingRule) {
          const pricePerCoin = matchingRule.pricePerCoin
          let bruttoPricePerCoin
          let price
          
          if (matchingRule.priceType === 'netto') {
            const vatAmount = pricePerCoin * 0.19
            bruttoPricePerCoin = pricePerCoin + vatAmount
            price = coins * bruttoPricePerCoin
          } else {
            bruttoPricePerCoin = pricePerCoin
            price = coins * pricePerCoin
          }
          
          return {
            coins: coins,
            price: Math.round(price * 100) / 100,
            pricePerCoin: Math.round(bruttoPricePerCoin * 1000) / 1000, 
            rule: matchingRule
          }
        } else {
          const bruttoPricePerCoin = 0.142 
          const totalPrice = coins * bruttoPricePerCoin
          
          return {
            coins: coins,
            price: Math.round(totalPrice * 100) / 100,
            pricePerCoin: bruttoPricePerCoin,
            rule: null
          }
        }
      })
    } else {
      coinPackages.value = getStandardPackages()
    }
  } catch (error) {
    console.error('Error loading pricing rules:', error)
    showError('Fehler beim Laden der Preisregeln')
    coinPackages.value = []
  } finally {
    loadingPackages.value = false
  }
}


const showPricingInfoModal = async () => {
  try {
    const rules = await pricingApi.getCoinPricingRules()
    
    const { showPricingInfoModal } = await import('@/services/notification')
    await showPricingInfoModal(rules)
    
  } catch (error) {
    console.error('Error showing pricing info:', error)
    showError('Fehler beim Laden der Preisübersicht')
  }
}

const showPurchaseDialog = async () => {
  try {
    if (coinPackages.value.length === 0) {
      await loadPricingRules()
    }
    
    if (coinPackages.value.length === 0) {
      coinPackages.value = getStandardPackages()
    }

    let lastBillingData = null
    try {
      const { getInvoiceData } = await import('@/api/invoiceData')
      const response = await getInvoiceData()
      if (response.success && response.data.invoiceData) {
        lastBillingData = response.data.invoiceData
      }
    } catch (error) {
      
    }

    // Navigate to purchase page
    router.push('/purchase')
    await authStore.updateProfile()
  } catch (error) {
    console.error('Error showing purchase dialog:', error)
    showError('Fehler beim Öffnen des Kaufdialogs')
  }
}

const showCouponPurchaseDialog = async () => {
  try {
    // Navigate to purchase page for coupon input
    router.push('/purchase')
  } catch (error) {
    console.error('Error navigating to purchase page:', error)
    showError('Fehler beim Öffnen der Kaufseite')
  }
}

const handlePurchase = async (purchaseData) => {
  try {
    purchasing.value = true

    if (purchaseData?.action === 'teamUpgrade') {
      await applyTeamCoupon(purchaseData)
      return
    }

    const result = await purchaseCoinsApi(
      purchaseData.coins,
      purchaseData.billingData,
      purchaseData.couponCode,
      purchaseData.paymentMethod,
    )

    if (result.requiresPayment && result.paypalOrder) {
      showSuccess('PayPal-Zahlung wird vorbereitet...')

      window.open(result.paypalOrder.approvalUrl, '_self', '')

      setTimeout(async () => {
        try {
          await authStore.updateProfile()
        } catch (error) {
          console.error('Error updating after PayPal payment:', error)
        }
      }, 5000)
    } else {
      const fallbackLabel =
        purchaseData.paymentMethod === 'bank_transfer'
          ? 'Zahlungsaufforderung'
          : 'Rechnung'
      showSuccess(
        result.message ||
          `${result?.billing?.coins || ''} Quittungen erfolgreich gekauft! Die ${fallbackLabel} wurde per E-Mail versendet.`,
      )
      await authStore.updateProfile()
    }

    if (purchaseHistoryRef.value) {
      purchaseHistoryRef.value.loadBillingHistory()
    }
  } catch (error) {
    console.error('Purchase error:', error)
    showError(error.response?.data?.message || 'Fehler beim Kauf der Quittungen')
  } finally {
    purchasing.value = false
  }
}

const applyTeamCoupon = async (upgradeData) => {
  const planType =
    upgradeData?.plan ||
    (upgradeData?.couponData?.coupon?.teamProPlan === 'ultimate' ? 'team_ultimate' : 'team_pro')
  const targetPlan = planType || 'team_pro'

  const billingData =
    upgradeData?.billingData && Object.keys(upgradeData.billingData).length > 0
      ? upgradeData.billingData
      : {}

  const result = await teamService.upgradeTeam(
    upgradeData.teamId,
    targetPlan,
    billingData,
    upgradeData.couponCode,
    'free',
  )

  const planLabel = targetPlan === 'team_ultimate' ? 'Ultimate' : 'Pro'
  const teamName = upgradeData.teamName || 'Bereich'

  showSuccess(
    `Bereich "${teamName}" wurde erfolgreich auf den ${planLabel} Plan upgegradet. Der Gutschein wurde angewendet.`,
  )

  await authStore.updateProfile()

  return result
}

onMounted(async () => {
  try {
    await loadPricingRules()
  } catch (error) {
    console.error('Error in onMounted:', error)
  }
})
</script>
